import { ReactNode, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconExclamationTriangleFilled, IconDownload } from '@iwoca/icons';
import { fetchPostLoanDocumentsSigned } from '@iwoca/lapi-client/edge';
import { Button, Modal } from '@iwoca/orion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SafeHTML, Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { LoanDocumentType, useGetDocument } from './useGetDocument';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { maintainStateKey } from '../../../../../iwRouter/LocationHelper';
import { HelpModalContent } from '../../../../navigation/helpModal/HelpModalContent';
import { ScrollPanel } from '../../../../ui/scrollPanel/ScrollPanel';
import { InnerLayout } from '../InnerLayout/InnerLayout';
import { useRequirementsProgress } from '../useRequirementsProgress';

const FUNDING_REQUIREMENTS_LIST_PATH = '/funding-requirements/';

type SignDocumentStepProps = {
    title: ReactNode;
    subtitle: ReactNode;
    buttonLabel: ReactNode;
    type: LoanDocumentType;
    requirementType: string;
};

export const SignDocumentStep = ({
    title,
    subtitle,
    buttonLabel,
    type,
    requirementType
}: SignDocumentStepProps) => {
    const stateKey = useStateKey();
    const history = useHistory();
    const { search } = useLocation();
    const queryClient = useQueryClient();
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [isScrollError, setIsScrollError] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const progress = useRequirementsProgress();

    const offerId = new URLSearchParams(search).get('offerId') ?? '';
    const amount = new URLSearchParams(search).get('amount') ?? '';

    const {
        documentId,
        documentHtml,
        downloadUrl,
        hasSigned,
        isLoading,
        isError
    } = useGetDocument(type);

    const {
        mutate: signDocument,
        isLoading: isSigning,
        isError: isSignError
    } = useMutation({
        mutationFn: () =>
            fetchPostLoanDocumentsSigned({
                stateKey,
                body: {
                    data: {
                        document_id: documentId!,
                        date_signed: new Date().toISOString().split('T')[0]
                    }
                }
            }),
        onSuccess: () => {
            if (type === 'guarantee') {
                tracking.ampli.fundingRequirementsPersonalGuaranteeSigned({
                    version: '2.0'
                });
                tracking.ampli.fundingRequirementCompleted({
                    requirement: 'personal_guarantee',
                    progress: { ...progress, personal_guarantee: true },
                    version: '2.0'
                });
            } else {
                tracking.ampli.fundingRequirementsCreditAgreementSigned({
                    version: '2.0'
                });
                tracking.ampli.fundingRequirementCompleted({
                    requirement: 'credit_agreement',
                    progress: { ...progress, credit_agreement: true },
                    version: '2.0'
                });
            }

            queryClient.removeQueries({
                queryKey: [
                    'fetchGetLoanDocumentsRequiredCreditFacility',
                    stateKey,
                    offerId,
                    amount
                ]
            });
            history.push(
                maintainStateKey({
                    pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                    search
                })
            );
        }
    });

    const handleSign = () => {
        if (!hasScrolledToBottom) {
            setIsScrollError(true);
            return;
        }
        signDocument();
    };

    const isSubmitError = isScrollError || isSignError;

    const DocumentErrorMessage = (
        <span>
            <FormattedMessage
                id="_.funding_requirements.sign_document_step.unable_to_get_documents"
                defaultMessage="We were unable to complete the signing documents process. Your account manager will be in contact soon, or give us a call."
            />
            <br />
            <button
                type="button"
                onClick={() => setIsHelpModalOpen(true)}
                className="cursor-pointer underline"
            >
                <FormattedMessage
                    id="_.funding_requirements.sign_document_step.contact_us"
                    defaultMessage="Contact Us"
                />
            </button>
        </span>
    );

    return (
        <InnerLayout
            title={title}
            subheading={subtitle}
            requirementType={requirementType}
        >
            {isError ? (
                <div className="mt-xl flex gap-l rounded-s border border-core-90 bg-core-95 px-m py-s text-m font-reg">
                    <IconExclamationTriangleFilled className="mt-s shrink-0 text-core-40" />
                    <div>{DocumentErrorMessage}</div>
                </div>
            ) : (
                <div className="mt-l overflow-hidden rounded-m border-2 border-secondary-40">
                    <ScrollPanel
                        type="div"
                        enabled={(!isLoading).toString()}
                        onBottomVisible={() => {
                            if (documentHtml) {
                                setHasScrolledToBottom(true);
                                setIsScrollError(false);
                            }
                        }}
                        className="max-h-[500px] overflow-y-auto p-l"
                        data-testid="sign-document-scroll-panel"
                    >
                        {isLoading && <DocumentSkeleton />}

                        {documentHtml && <SafeHTML html={documentHtml} />}
                    </ScrollPanel>
                </div>
            )}

            {!isError && (
                <>
                    {(!hasScrolledToBottom || isSubmitError) && (
                        <div
                            className={`mt-xl flex gap-l rounded-xs px-m py-s ${isSignError ? 'text-core-40' : 'text-primary-10'} ${isSubmitError ? 'border border-core-90 bg-core-95' : 'border border-secondary-80 bg-secondary-95'}`}
                            role={isSubmitError ? 'alert' : undefined}
                        >
                            <IconExclamationTriangleFilled
                                className={`mt-xs shrink-0 text-xl ${isSubmitError ? 'text-core-40' : 'text-primary-10'}`}
                            />
                            <p className="m-0 font-bold">
                                {isSignError ? (
                                    DocumentErrorMessage
                                ) : (
                                    <FormattedMessage
                                        id="_.funding_requirements.sign_document_step.scroll_message"
                                        defaultMessage="You must scroll down through the entire document to continue."
                                    />
                                )}
                            </p>
                        </div>
                    )}

                    <div className="mt-2xl flex items-center gap-l">
                        <a
                            href={downloadUrl}
                            download
                            aria-label="Download document"
                            className="flex h-3xl w-3xl items-center justify-center rounded-full border border-primary-10 hover:!bg-structure-95"
                            onClick={() =>
                                tracking.ampli.fundingRequirementsDocumentDownloaded(
                                    {
                                        document_type:
                                            type === 'guarantee'
                                                ? 'personal_guarantee'
                                                : 'credit_agreement',
                                        version: '2.0'
                                    }
                                )
                            }
                        >
                            <IconDownload className="text-2xl text-primary-10" />
                        </a>
                        <Button
                            variant="primary-rounded"
                            disabled={hasSigned || isError || isLoading}
                            loading={isSigning}
                            onClick={handleSign}
                            className="grow"
                        >
                            {buttonLabel}
                        </Button>
                    </div>
                </>
            )}
            <Modal
                headerBar
                isOpen={isHelpModalOpen}
                onClose={() => setIsHelpModalOpen(false)}
            >
                <HelpModalContent />
            </Modal>
        </InnerLayout>
    );
};

const DocumentSkeleton = () => (
    <div data-testid="document-loading-skeleton">
        {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="my-l h-xl w-full" />
        ))}
    </div>
);
